import clsx from "clsx";
import React, { ReactNode } from "react";

import { Icon } from "../Icon";

export type ButtonColor =
  | "amber"
  | "blue"
  | "cyan"
  | "dark"
  | "dark/white"
  | "dark/zinc"
  | "emerald"
  | "fuchsia"
  | "green"
  | "indigo"
  | "light"
  | "lime"
  | "orange"
  | "pink"
  | "purple"
  | "red"
  | "rose"
  | "sky"
  | "teal"
  | "violet"
  | "white"
  | "yellow"
  | "zinc";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLElement> {
  children?: ReactNode;
  color?: ButtonColor;
  href?: string;
  htmlFor?: string;
  icon?: React.ComponentProps<typeof Icon>["name"];
  iconClassName?: string;
  isLoading?: boolean;
  plain?: boolean;
  size?: "2xs" | "lg" | "md" | "sm" | "xs";
}

export default function Button({
  color = "dark/zinc",
  plain = false,
  href,
  htmlFor,
  icon,
  children,
  disabled,
  onClick,
  className,
  type = "button",
  isLoading = false,
  size = "md",
  iconClassName,
  ...props
}: ButtonProps) {
  const buttonClasses = clsx(
    "inline-flex items-center justify-center rounded-md  font-semibold transition",
    size === "2xs" ? "gap-1.5" : "gap-2",
    // Compact enough to sit inline beside a row of content.
    size === "2xs" && "px-2 py-1 text-[10px]",
    size === "sm" && "px-1 md:px-2 py-1 md:py-2 text-[10px] md:text-xs",
    size === "md" && "px-3 py-2 text-xs md:text-sm",
    size === "lg" && "px-3 md:px-4 py-2 md:py-3 text-sm md:text-base",
    size === "xs" && "px-0 py-1 text-[9px] md:text-xs",
    // Base styles
    !plain && "shadow-sm",
    // Color variants
    {
      "bg-blue-600 text-white hover:bg-blue-500 dark:hover:bg-blue-400":
        color === "blue" && !plain,
      "bg-cyan-600 text-white hover:bg-cyan-500 dark:hover:bg-cyan-400":
        color === "cyan" && !plain,
      "bg-gray-800 text-white hover:bg-zinc-700 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100":
        color === "dark/zinc" && !plain,
      "bg-green-600 text-white hover:bg-green-500": color === "green" && !plain,
      "bg-red-600 text-white hover:bg-red-500": color === "red" && !plain,
      "bg-white border border-gray-200 text-zinc-900 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-white dark:hover:bg-zinc-800":
        color === "light" && !plain,
      "text-zinc-900 hover:bg-zinc-100 dark:text-white dark:hover:bg-zinc-800/50":
        plain,
    },
    // Loading & Disabled states
    (disabled || isLoading) && "opacity-50 cursor-not-allowed",
    isLoading && "cursor-wait",
    // File input label gets cursor-pointer
    htmlFor && "cursor-pointer",
    className,
  );

  const LoadingSpinner = () => (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const ButtonContent = () => (
    <>
      {isLoading && <LoadingSpinner />}
      {!isLoading && icon && (
        <Icon
          name={icon}
          data-testid={icon}
          className={clsx("h-3 md:h-4 w-3 md:w-4", iconClassName)}
        />
      )}
      {children}
    </>
  );

  if (htmlFor && !disabled && !isLoading) {
    return (
      <label htmlFor={htmlFor} className={buttonClasses} {...props}>
        <ButtonContent />
      </label>
    );
  }

  if (href && !disabled && !isLoading) {
    return (
      <a
        href={href}
        className={buttonClasses}
        onClick={onClick ? onClick : () => {}}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        <ButtonContent />
      </a>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled || isLoading}
      className={buttonClasses}
      onClick={onClick}
      {...props}
    >
      <ButtonContent />
    </button>
  );
}
